/**
 * ToB Sales AI Assistant - TypeScript 类型定义
 */

// ============================================
// 基础类型
// ============================================

export type UUID = string

export type DealStage = 'lead' | 'qualify' | 'proposal' | 'negotiate' | 'closed_won' | 'closed_lost'
export type CustomizationLevel = 'low' | 'medium' | 'high'
export type ProcurementType = 'bidding' | 'sole_source' | 'inquiry'

export type PersonRole = 'EB' | 'TB' | 'UB' | 'Coach' | 'Other'
export type PersonAttitude = 'support' | 'neutral' | 'oppose' | 'unknown'
export type InfluenceLevel = 'high' | 'medium' | 'low'

export type EvidenceSourceType = 'meeting_note' | 'im' | 'email' | 'audio' | 'document' | 'manual'
export type EvidenceStatus = 'pending' | 'confirmed' | 'corrected' | 'deleted'
export type DemandLayer = 'solution' | 'organization' | 'personal'
export type SignalStrength = 'strong' | 'medium' | 'weak'

export type HypothesisDimension = 'customer' | 'demand' | 'value' | 'barrier' | 'risk'
export type HypothesisStatus = 'unknown' | 'plausible' | 'confirmed' | 'falsified'
// 风险等级（派生字段，统一来源）
export type RiskLevel = 'none' | 'key_pending' | 'fatal_gap'

export type ActionType = 'question' | 'activity' | 'artifact' | 'touchpoint'
export type ActionGrade = 'A' | 'B' | 'C'
export type ActionStatus = 'proposed' | 'accepted' | 'done' | 'rejected' | 'not_applicable'

// 输出件类型（用于动作与输出件联动）
export type ArtifactType = 'one_pager' | 'decision_triangle' | 'visit_canvas'

// 输出件类型显示名称映射
export const ARTIFACT_TYPE_NAMES: Record<ArtifactType, string> = {
  one_pager: '组织层闭环一页纸',
  decision_triangle: '决策三角形翻译表',
  visit_canvas: '拜访画布',
}

export type HealthStatus = 'cold' | 'red' | 'yellow' | 'green'

// ============================================
// 实体类型
// ============================================

export interface Deal {
  id: UUID
  name: string
  stage: DealStage
  customerName?: string
  industry?: string
  amountEst?: number
  customizationLevel?: CustomizationLevel
  procurementType?: ProcurementType
  expectedCloseDate?: string
  dealUrgencyDays?: number
  score: number
  confidence: number
  ownerId?: UUID
  lastActionGradeUsed?: ActionGrade
  notes?: string
  createdAt: string
  updatedAt: string
  
  // 聚合统计
  evidenceCount?: number
  personCount?: number
  fatalGapCount?: number
  
  // 体检卡状态
  healthStatus?: HealthStatus
  fatalGap?: string
}

export interface Person {
  id: UUID
  dealId: UUID
  name: string
  title?: string
  department?: string
  phone?: string
  email?: string
  wechat?: string
  primaryRole: PersonRole
  roleTags: PersonRole[]
  attitude: string  // 支持/中立/反对/未知
  influence?: InfluenceLevel
  isConfirmedEb: boolean
  ebAccessibility?: string
  focusAreas?: string[]
  styleTags?: string[]
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface Evidence {
  id: UUID
  dealId: UUID
  sourceType: EvidenceSourceType
  sourceLink?: string
  sourceFileName?: string
  contentRaw: string
  contentSummary?: string
  extractedTags: ExtractedTags
  demandLayer?: DemandLayer
  demandLayerConfidence?: number
  signalStrength: SignalStrength
  signalScore: number
  signalType?: string
  status: EvidenceStatus
  userCorrection?: Record<string, unknown>
  extractionConfidence: number
  occurredAt: string
  createdAt: string
  updatedAt: string
  
  // 分析结果（可选）
  analysisResult?: EvidenceAnalysisResult
}

export interface ExtractedTags {
  persons: ExtractedPerson[]
  facts: ExtractedFact[]
  riskFlags: string[]
  groundingSpans?: GroundingSpan[]
}

export interface ExtractedPerson {
  name: string
  role?: string
  attitude?: string
  title?: string
  department?: string
  spanStart?: number
  spanEnd?: number
}

export interface ExtractedFact {
  category: string
  content: string
  spanStart?: number
  spanEnd?: number
}

export interface GroundingSpan {
  start: number
  end: number
  label: string
  text: string
}

export interface EvidenceAnalysisResult {
  persons: ExtractedPerson[]
  facts: ExtractedFact[]
  demandLayer: DemandLayer
  demandLayerConfidence: number
  signalStrength: SignalStrength
  signalType?: string
  signalScore: number
  riskFlags: string[]
  groundingSpans: GroundingSpan[]
  summary: string
  followUpQuestion?: string
  missingInfoType?: string
}

export interface Hypothesis {
  id: UUID
  dealId: UUID
  dimension: HypothesisDimension
  category: string
  statement: string
  status: HypothesisStatus
  confidence: number
  evidenceIds: UUID[]
  reasoning?: string
  changeHistory: ChangeHistoryItem[]
  impactScore: number
  // 风险等级（派生字段，统一来源）
  riskLevel?: RiskLevel
  // isFatalGap 由 riskLevel 派生，保留用于兼容
  isFatalGap: boolean
  createdAt: string
  updatedAt: string
}

export interface ChangeHistoryItem {
  timestamp: string
  oldStatus: HypothesisStatus
  newStatus: HypothesisStatus
  triggerEvidenceId?: UUID
  reason: string
}

export interface Action {
  id: UUID
  hypothesisId: UUID
  actionType: ActionType
  actionGrade: ActionGrade
  depthLevel: 'Full' | 'Quick' | 'Defense'
  content: string
  timeCostDays?: number
  timeCostDisplay?: string
  targetPersonId?: UUID
  targetPersonName?: string
  priority: 'P0' | 'P1' | 'P2'
  status: ActionStatus
  idealScenario?: string
  fallbackActionId?: UUID
  warning?: string
  expectedSignals?: SignalExpectation[]
  // 关联输出件类型（用于显示生成输出件入口）
  artifactRef?: ArtifactType
  // 待办事项：计划执行日期（ISO 日期字符串，如 "2026-01-28"）
  scheduledDate?: string
  // 待办事项：来源假设/风险项标题（用于待办列表展示）
  hypothesisTitle?: string
  createdAt: string
  updatedAt: string
}

export interface SignalExpectation {
  id?: string  // 信号ID（用于标记观测）
  strength: SignalStrength
  description: string
  observed?: boolean  // 是否已观测
  observedAt?: string  // 观测时间
  observedEvidenceId?: string  // 关联证据ID
  observedNotes?: string  // 观测备注
}

// ============================================
// 体检卡类型
// ============================================

export interface HealthCardState {
  stateType: 'cold' | 'red' | 'gray' | 'yellow' | 'green'
  color: string
  icon: string
  title: string
  description: string
  quote?: string
  actionText: string
  score: string
  scoreColor: string
  tag: string
  tagColor: string
}

export interface FatalGapInfo {
  gapType: string
  dimension: HypothesisDimension
  title: string
  description: string
  confidence: number
  impactScore: number
  evidenceRefs: { id: string; summary: string }[]
  recommendedActions: Action[]
  // TASK-H7: 风险等级支持
  riskLevel?: RiskLevel
  hypothesisId?: string  // 用于跳转联动
  status?: HypothesisStatus
}

export interface ScoreBreakdown {
  baseScore: number
  deductions: { type: string; points: number; reason: string }[]
  additions: { type: string; points: number; reason: string }[]
  finalScore: number
}

export interface HealthCardResponse {
  dealId: UUID
  dealName: string
  score: number
  confidence: number
  scoreBreakdown?: ScoreBreakdown
  cardState: HealthCardState
  fatalGaps: FatalGapInfo[]
  nextActions: Action[]
  evidenceCount: number
  lastEvidenceDate?: string
  daysWithoutStrongSignal?: number
  displayMode: 'ideal' | 'fallback_low_confidence' | 'fallback_multiple_gaps' | 'cold_start' | 'stall_warning' | 'fatal_gap' | 'change_alert_error' | 'change_alert_warning'
}

// ============================================
// 动作分级类型
// ============================================

export interface GradeActions {
  grade: ActionGrade
  label: string
  description: string
  timeCostDays: number
  scenario: string
  actions: Action[]
}

export interface ActionsByGradeResponse {
  diagnosis: {
    type: string
    confidence: number
  }
  recommendedGrade: ActionGrade
  actionsByGrade: Record<ActionGrade, GradeActions>
  userControl: {
    canSwitchGrade: boolean
    canMarkNotApplicable: boolean
  }
}

// ============================================
// T10: 话术变体与成功判据类型
// ============================================

/**
 * 话术类型枚举
 * BR-T10-01: 6种话术类型
 */
export type ScriptType = 'explore' | 'align' | 'push' | 'pressure' | 'retreat' | 'stop'

/**
 * 话术类型配置映射
 */
export const SCRIPT_TYPE_CONFIG: Record<ScriptType, {
  label: string
  purpose: string
  color: string
  bgColor: string
}> = {
  explore: { label: '探测型', purpose: '获取信息', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  align: { label: '对齐型', purpose: '建立共识', color: 'text-green-600', bgColor: 'bg-green-50' },
  push: { label: '推进型', purpose: '推动下一步', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  pressure: { label: '施压型', purpose: '提升优先级', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  retreat: { label: '退让型', purpose: '建立信任', color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
  stop: { label: '停止型', purpose: '防止内耗', color: 'text-red-600', bgColor: 'bg-red-50' },
}

/**
 * 话术变体接口
 * BR-T10-02: 包含type/template/purpose
 */
export interface ScriptVariant {
  id: string
  type: ScriptType
  template: string
  purpose: string
  scenario?: string
  tips?: string
}

/**
 * 成功判据接口
 * BR-T10-03: 包含success/partial/fail/timeoutDays
 */
export interface SuccessCriteria {
  success: string
  partial: string
  fail: string
  timeoutDays: number
}

/**
 * 带话术的动作接口（扩展 Action）
 */
export interface ActionWithScript extends Action {
  scriptVariants?: ScriptVariant[]
  successCriteria?: SuccessCriteria
  planBActionId?: UUID
  planBActionContent?: string
}

/**
 * 扩展 GradeActions 以支持话术
 */
export interface GradeActionsWithScript extends GradeActions {
  actions: ActionWithScript[]
}

// ============================================
// 假设树类型
// ============================================

export interface HypothesisSummary {
  id: string
  category?: string
  statement: string
  status: HypothesisStatus
  confidence: number
  // 风险等级（派生字段，统一来源）
  riskLevel?: RiskLevel
  // isFatalGap 由 riskLevel 派生，保留用于兼容
  isFatalGap: boolean
  evidenceCount: number
  reasoning?: string
  updatedAt?: string
  // 冲突通知：是否有未处理的冲突（TASK-H5）
  hasUnresolvedConflicts?: boolean
  unresolvedConflictCount?: number
}

// ============================================
// 冲突通知类型（TASK-H5）
// ============================================

export type ConflictSource = 'llm_diagnosis' | 'signal_observation'
export type ConflictResolution = 'keep' | 'rollback_and_update'

export interface ConflictNotification {
  id: string
  hypothesisId: string
  dealId: string
  currentStatus: string
  suggestedStatus: string
  evidenceId?: string
  signalId?: string
  reason: string
  source: ConflictSource
  createdAt: string
  resolved: boolean
  resolvedBy?: string
  resolvedAt?: string
  resolution?: ConflictResolution
}

export interface ConflictListResponse {
  dealId: string
  conflicts: ConflictNotification[]
  total: number
  unresolvedCount: number
}

export interface HypothesisByDimensionResponse {
  dealId: UUID
  dealName: string
  dimensions: Record<HypothesisDimension, HypothesisSummary[]>
}

// ============================================
// T9-T11 升级：阶段感知诊断类型
// ============================================

export type ChangeAlertType = 
  | 'stance_flip' 
  | 'budget_change' 
  | 'competitor_entry' 
  | 'key_person_change' 
  | 'window_change' 
  | 'contact_silent'

export type ChangeAlertSeverity = 'error' | 'warning' | 'info'

export interface ChangeAlert {
  id: UUID
  dealId: UUID
  type: ChangeAlertType
  severity: ChangeAlertSeverity
  title: string
  description?: string
  changeDetail?: Record<string, unknown>
  recommendedAction?: string
  dismissed: boolean
  dismissedBy?: UUID
  dismissedAt?: string
  dismissReason?: string
  createdAt: string
}

/**
 * 阶段升级建议（重构新增）
 * 当证据显示项目可能处于更高阶段时返回
 */
export interface StageSuggestion {
  suggestedStage: string
  suggestedStageDisplay: string
  reason: string
  confidence: number
  keywords: string[]
}

export interface StageJudgement {
  dealId: UUID
  stageAuto: DealStage
  stageFinal: DealStage
  stageDisplay: string
  confidence: number
  confidenceDisplay: string
  decidedBy: string
  isOverridden: boolean
  overrideInfo?: {
    stage: DealStage
    reason?: string
    expiresAt?: string
    needsConfirmation?: boolean
  }
  evidenceRefs: string[]
  featuresSnapshot: Record<string, unknown>
  rulesMatched: { ruleId: string; description: string; stage: string }[]
  // 重构新增字段
  source: 'user_declared' | 'override'  // 阶段来源
  stageSuggestion?: StageSuggestion      // 阶段升级建议（证据推断）
}

/**
 * 分类假设进度项
 */
export interface CategoryProgressItem {
  category: string   // 假设类别代码
  name: string       // 假设中文名称
  status: 'confirmed' | 'falsified' | 'pending' | 'unknown'
}

/**
 * 分类进度（必须确认/必须证伪/观察项）
 */
export interface CategoryProgress {
  total: number
  completed: number
  rate: number
  items: CategoryProgressItem[]
}

/**
 * 阶段升级条件
 */
export interface UpgradeCondition {
  description: string
  isMet: boolean
  category?: string
}

/**
 * 阶段完成度项 - 匹配后端 StageCompletionItem
 */
export interface StageCompletionItem {
  stage: string
  stageDisplay: string
  isCurrent: boolean
  isCompleted: boolean
  completionRate: number
  requiredSignals: number
  confirmedSignals: number
  missingSignals: string[]
  // 新增：分类进度
  mustConfirmProgress?: CategoryProgress
  mustFalsifyProgress?: CategoryProgress
  observeProgress?: CategoryProgress
  // 新增：升级条件
  upgradeConditions?: UpgradeCondition[]
}

/**
 * 阶段完成度响应 - 匹配后端 StageCompletionResponse
 */
export interface StageCompletion {
  dealId: string
  currentStage: string
  stages: StageCompletionItem[]
  overallProgress: number
  canUpgrade: boolean
  upgradeSuggestion?: string
}

export interface StageAwareDiagnosisResponse {
  dealId: UUID
  dealName: string
  diagnosedAt: string
  stageJudgement?: StageJudgement
  hypothesisUpdates: {
    hypothesisId: UUID
    category: string
    currentStatus: HypothesisStatus
    confidence: number
    recentChange?: ChangeHistoryItem
  }[]
  healthCard: HealthCardResponse
  actionBundle: {
    actionsCount: number
    actions: {
      id: UUID
      content: string
      actionGrade: ActionGrade
      priority: string
      timeCostDisplay?: string
    }[]
    error?: string
  }
  changeAlerts: ChangeAlert[]
  stageCompletion: StageCompletion
}

// 扩展 HealthCardResponse 以支持变更预警
export interface EnhancedHealthCardResponse extends HealthCardResponse {
  stageJudgement?: StageJudgement
  stageCompletion?: StageCompletion
  changeAlerts?: ChangeAlert[]
}

// ============================================
// 按假设分组的动作响应类型
// ============================================

/**
 * 单个假设（风险项）的动作分组
 */
export interface HypothesisActions {
  hypothesisId: string
  hypothesisTitle: string
  category: string
  isFatalGap: boolean
  recommendedGrade: ActionGrade
  actionsByGrade: Record<ActionGrade, GradeActions>
}

/**
 * 按假设分组的动作响应
 */
export interface ActionsByHypothesisResponse {
  hypotheses: HypothesisActions[]
  totalTimeCostDays: number
  recommendedGrade: ActionGrade
}

// ============================================
// 三层需求类型（T1模板）
// ============================================

/**
 * 需求层级类型
 */
export type DemandLayerType = 'organization' | 'personal' | 'solution'

/**
 * 需求层级状态
 */
export type DemandLayerStatus = 'incomplete' | 'partial' | 'complete'

/**
 * 三层需求项
 */
export interface DemandLayerItem {
  id: string
  dealId: string
  layer: DemandLayerType
  statement: string | null       // 当前表述（客户口径优先）
  evidenceIds: string[]          // 关联证据ID列表
  gapDescription: string | null  // 缺口描述
  nextQuestion: string | null    // 下一步验证问题
  status: DemandLayerStatus
  updatedAt: string
}

/**
 * 三层需求响应
 */
export interface DemandLayersResponse {
  dealId: string
  layers: DemandLayerItem[]
}

/**
 * 三层需求更新请求
 */
export interface DemandLayerUpdate {
  statement?: string
  evidenceIds?: string[]
  gapDescription?: string
  nextQuestion?: string
  status?: DemandLayerStatus
}

/**
 * 三层需求配置
 */
export const DEMAND_LAYER_CONFIG: Record<DemandLayerType, {
  label: string
  description: string
  color: string
  bgColor: string
  iconColor: string
}> = {
  organization: {
    label: '组织层（业务闭环）',
    description: '主要矛盾、成功口径、业务目标',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    iconColor: 'text-purple-500',
  },
  personal: {
    label: '个人层（收益/责任/风险）',
    description: '个人收益、责任承担、风险规避',
    color: 'text-pink-700',
    bgColor: 'bg-pink-100',
    iconColor: 'text-pink-500',
  },
  solution: {
    label: '方案层（手段/清单）',
    description: '功能需求、技术参数、实施清单',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    iconColor: 'text-blue-500',
  },
}
